/**
 * assets/popbar-cart.js
 * =====================
 * Popbar Phase C — native flavor-picker cart integration for Prestige theme.
 *
 * PURPOSE
 *   Provides a single Ajax cart facade for the 6/5-slot flavor packs so that
 *   line_item.key (not variant_id) is always used for /cart/change.js, section
 *   rendering happens in one round trip, and Prestige's `cart:refresh` event
 *   fires exactly once per mutation.
 *
 * INVARIANTS ENFORCED  (see CLAUDE.md for the full list)
 *   - #1  /cart/change.js `id` is always a string
 *   - #2  /cart/change.js identifies lines by line_item.key, never variant_id
 *   - #3  Every mutation includes `sections=` for bundled rendering
 *   - #5  Properties on /cart/change.js REPLACE — we always merge first
 *   - #10 Every successful mutation dispatches `cart:refresh` on
 *         document.documentElement with bubbles:true
 *
 * DOC SOURCES
 *   https://shopify.dev/docs/api/ajax/reference/cart
 *   https://shopify.dev/docs/api/ajax/section-rendering
 *   https://support.maestrooo.com/article/789-prestige-theme-changelog
 *
 * DO NOT edit compiled assets/storefront-modules-*.js. Add overrides here.
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Constants
  // ---------------------------------------------------------------------------

  var SECTIONS = 'cart-drawer,main-cart-items,cart-icon-bubble';

  // Native-picker handles — MUST match the constant in CLAUDE.md and the
  // Liquid guard in layout/theme.liquid. Verify before shipping.
  // Actual store handles — verified against Shopify Admin 2026-08-31
  // (Kit shipped with incorrect handles; corrected here)
  var POPBAR_NATIVE_PICKER_HANDLES = [
    'mixed-pack',
    'popgelato-pack',
    'popsorbetto-pack',
    'popgelato-minis',
    'popsorbetto-minis',
    'mixed-gelato-sorbetto-minis'
  ];

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // ---------------------------------------------------------------------------
  // Small utilities
  // ---------------------------------------------------------------------------

  function log() {
    if (!window.POPBAR_DEBUG) return;
    var args = ['[popbar-cart]'].concat([].slice.call(arguments));
    console.log.apply(console, args);
  }

  function warn() {
    var args = ['[popbar-cart]'].concat([].slice.call(arguments));
    console.warn.apply(console, args);
  }

  function dispatchCartRefresh() {
    // Prestige documented event since v4.5.5. MUST be on documentElement
    // with bubbles:true — dispatching on window is silently ignored.
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true })
    );
  }

  function applySections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (sectionId) {
      var html = sections[sectionId];
      if (!html) return; // Shopify returns null for sections it can't find
      var target = document.getElementById('shopify-section-' + sectionId)
                || document.querySelector('[data-section-id="' + sectionId + '"]');
      if (!target) {
        log('applySections: no target for', sectionId);
        return;
      }
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var replacement = doc.getElementById('shopify-section-' + sectionId);
      target.innerHTML = replacement ? replacement.innerHTML : html;
    });
  }

  // In-flight mutation lock. Cart Ajax API has an unpublished throttle;
  // rapid-fire calls trigger 429s that cascade into drawer breakage.
  // See REFERENCES.md §1.6.
  var mutationChain = Promise.resolve();
  function serialize(fn) {
    var next = mutationChain.then(fn, fn);
    // Swallow rejection into the chain so one failure doesn't poison later calls,
    // but re-surface the actual promise to the caller.
    mutationChain = next.catch(function () {});
    return next;
  }

  // ---------------------------------------------------------------------------
  // Public: add a configured pack to the cart
  //
  //   popbarAddPackToCart({
  //     variantId:  40000000000000,        // integer
  //     quantity:   1,
  //     flavors:    ['Chocolate', 'Strawberry', 'Pistachio', 'Vanilla', 'Mango'],
  //     packMetadata: {
  //       pack_size: '5',
  //       pack_type: 'mini-mixed',
  //       pricing_model: 'A',
  //       picker_version: 'phase-c-2026-09'
  //     }
  //   })
  // ---------------------------------------------------------------------------
  function popbarAddPackToCart(opts) {
    return serialize(function () {
      var variantId  = opts.variantId;
      var quantity   = typeof opts.quantity === 'number' ? opts.quantity : 1;
      var flavors    = Array.isArray(opts.flavors) ? opts.flavors : [];
      var metadata   = opts.packMetadata || {};

      if (!variantId) {
        return Promise.reject(new Error('popbarAddPackToCart: variantId is required'));
      }

      // Build properties: visible flavor slots + underscore-hidden metadata.
      // Invariant #6: 'Flavor N' Title Case + space + number (fulfillment reads this shape).
      // Invariant #9: never emit empty string values.
      var properties = {};
      flavors.forEach(function (flavor, index) {
        if (flavor) properties['Flavor ' + (index + 1)] = String(flavor);
      });
      // Invariant #7: single-underscore prefix for hidden internal props.
      Object.keys(metadata).forEach(function (key) {
        var v = metadata[key];
        if (v === undefined || v === null || v === '') return;
        properties['_' + key] = String(v);
      });

      var payload = {
        items: [{
          id: variantId,     // /cart/add.js accepts integer or string here
          quantity: quantity,
          properties: properties
        }],
        sections: SECTIONS,
        sections_url: window.location.pathname
      };

      log('add.js payload', payload);

      return fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      }).then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, status: response.status, data: data };
        });
      }).then(function (result) {
        if (!result.ok) {
          warn('add.js failed', result.status, result.data);
          throw new Error('cart/add failed: ' + result.status
            + ' ' + (result.data && result.data.description || ''));
        }
        applySections(result.data.sections);
        dispatchCartRefresh();
        return result.data;
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Public: change (update qty or properties) an existing cart line
  //
  //   popbarCartChange({
  //     lineKey:    '40000000000000:abc123def456…',   // MUST be string
  //     quantity:   2,                                 // optional
  //     properties: { 'Flavor 3': 'Mango' }            // optional (see merge note)
  //   })
  //
  //   NOTE on properties: /cart/change.js REPLACES the entire properties
  //   object. If you pass { 'Flavor 3': 'Mango' } and the line previously had
  //   Flavor 1 / 2, they will be ERASED. This helper handles the merge for you
  //   when `mergeProperties: true` is passed (default false — explicit).
  // ---------------------------------------------------------------------------
  function popbarCartChange(opts) {
    return serialize(function () {
      var lineKey = opts.lineKey;
      if (!lineKey || typeof lineKey !== 'string') {
        return Promise.reject(new Error(
          'popbarCartChange: lineKey must be a string (line_item.key). '
          + 'Never pass variant_id here — see CLAUDE.md invariant #2.'
        ));
      }

      var setup;
      if (opts.mergeProperties && opts.properties) {
        // Fetch current cart, find our line, merge props.
        setup = fetch(ROOT + 'cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var currentLine = (cart.items || []).find(function (item) {
              return item.key === lineKey;
            });
            var merged = Object.assign({}, (currentLine && currentLine.properties) || {}, opts.properties);
            // Drop empty-string values (invariant #9)
            Object.keys(merged).forEach(function (k) {
              if (merged[k] === '' || merged[k] === null || merged[k] === undefined) delete merged[k];
            });
            return merged;
          });
      } else {
        setup = Promise.resolve(opts.properties);
      }

      return setup.then(function (finalProperties) {
        var body = {
          id: lineKey,                          // string — invariant #1 & #2
          sections: SECTIONS,                   // invariant #3
          sections_url: window.location.pathname
        };
        if (typeof opts.quantity === 'number') body.quantity = opts.quantity;
        if (finalProperties && typeof finalProperties === 'object') {
          body.properties = finalProperties;    // full replace — invariant #5
        }

        log('change.js payload', body);

        return fetch(ROOT + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(body)
        }).then(function (response) {
          return response.json().then(function (data) {
            return { ok: response.ok, status: response.status, data: data };
          });
        }).then(function (result) {
          if (!result.ok) {
            warn('change.js failed', result.status, result.data);
            // Invariant: do NOT auto-retry — a 400 means the payload is wrong.
            // Retrying only doubles the error count.
            throw new Error('cart/change failed: ' + result.status
              + ' ' + (result.data && result.data.description || ''));
          }
          applySections(result.data.sections);
          dispatchCartRefresh();
          return result.data;
        });
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Public: remove a cart line by line_item.key
  //   Shortcut for popbarCartChange({ lineKey, quantity: 0 })
  // ---------------------------------------------------------------------------
  function popbarCartRemove(lineKey) {
    return popbarCartChange({ lineKey: lineKey, quantity: 0 });
  }

  // ---------------------------------------------------------------------------
  // Public: read cart, filtered for popbar-relevant lines
  // ---------------------------------------------------------------------------
  function popbarGetCart() {
    return fetch(ROOT + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function isPopbarNativePack(item) {
    if (!item) return false;
    // Prefer handle if we have it; fall back to product_id.
    if (item.handle && POPBAR_NATIVE_PICKER_HANDLES.indexOf(item.handle) > -1) return true;
    if (item.product_handle && POPBAR_NATIVE_PICKER_HANDLES.indexOf(item.product_handle) > -1) return true;
    return false;
  }

  // ---------------------------------------------------------------------------
  // Event delegation: wire up Remove buttons in the cart drawer.
  //
  //   Requires markup:
  //     <a data-popbar-remove data-line-key="{{ line_item.key }}" href="…">Remove</a>
  //
  //   The plain-anchor href fallback (invariant #12) means Remove still works
  //   even if this JS never loads.
  // ---------------------------------------------------------------------------
  function bindRemoveDelegation(root) {
    (root || document).addEventListener('click', function (event) {
      var trigger = event.target.closest && event.target.closest('[data-popbar-remove]');
      if (!trigger) return;
      var lineKey = trigger.getAttribute('data-line-key');
      if (!lineKey) return;
      event.preventDefault();
      trigger.setAttribute('aria-busy', 'true');
      popbarCartRemove(lineKey)
        .catch(function (err) {
          warn('remove failed, falling back to full page reload', err);
          // Fallback: navigate to the raw remove URL from the anchor.
          if (trigger.tagName === 'A' && trigger.href) window.location.href = trigger.href;
        })
        .finally(function () {
          trigger.removeAttribute('aria-busy');
        });
    }, false);
  }

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------
  function boot() {
    bindRemoveDelegation(document);
    log('booted', {
      native_handles: POPBAR_NATIVE_PICKER_HANDLES,
      sections: SECTIONS,
      root: ROOT
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // ---------------------------------------------------------------------------
  // Public API surface (assigned to window)
  // ---------------------------------------------------------------------------
  window.popbarCart = {
    add:    popbarAddPackToCart,
    change: popbarCartChange,
    remove: popbarCartRemove,
    get:    popbarGetCart,
    isNativePack: isPopbarNativePack,
    HANDLES: POPBAR_NATIVE_PICKER_HANDLES.slice(),
    _internal: { applySections: applySections, dispatchCartRefresh: dispatchCartRefresh }
  };
})();
